import type { AscEndpointCategory } from "./project.js";

/**
 * `content_automation` (A7Y-4478) is the role an automation principal's content
 * key carries: drafts only — it can read, create and edit drafts, duplicate and
 * preview, and never publish, schedule, unpublish, archive, delete or restore.
 * `content_publisher` (A7Y-5078) is an automation key minted with `canPublish`:
 * everything `content_automation` can do, plus publish, schedule, unpublish and
 * archive, and editing a live document's working copy. Delete and restore stay
 * with a person.
 */
export type AdminRole =
  | "super_admin"
  | "support"
  | "billing_admin"
  | "admin"
  | "content_automation"
  | "content_publisher"
  | "marketing";

/**
 * A7Y-4957 — the staff roles a superadmin can assign from Admin → Users. Stored
 * as `Users/{id}.adminRole`; `superadmin: true` still means full access and wins
 * over this field. `marketing` reaches only the endpoints on the allowlist in
 * `a7y-api/src/middleware/adminRoleScope.ts`.
 */
export const ASSIGNABLE_ADMIN_ROLES = ["marketing"] as const;
export type AssignableAdminRole = (typeof ASSIGNABLE_ADMIN_ROLES)[number];

export interface AdminUser {
  id: string;
  email: string;
  role: AdminRole;
  permissions?: string[];
  createdAt: number | string;
  updatedAt?: number | string;
  mfaEnabled?: boolean;
  isActive?: boolean;
  name?: string;
}

export type AdminAuditAction =
  | "login"
  | "logout"
  | "mfa_verify"
  | "org_suspend"
  | "org_reinstate"
  | "org_view"
  | "user_delete"
  | "user_bulk_delete"
  | "user_disable"
  | "user_enable"
  | "user_password_reset"
  | "user_update"
  | "user_view"
  | "billing_credit_issue"
  | "billing_invoice_view"
  | "billing_ledger_view"
  | "support_ticket_view"
  | "support_ticket_reply"
  | "support_ticket_close"
  | "system_config_update"
  | "system_health_check"
  | "system_metrics_view"
  | "audit_log_view"
  | "org_override_plan"
  | "org_trial_update"
  | "user_remove_from_org"
  | "user_change_role"
  | "user_admin_role_update"
  | "feature_flag_view"
  | "feature_flag_update"
  | "feature_status_view"
  | "feature_status_waiting_view"
  | "feature_status_update"
  | "subscription_view"
  | "promo_coupon_view"
  | "promo_coupon_create"
  | "promo_coupon_delete"
  | "promo_code_create"
  | "promo_code_delete"
  | "short_link_view"
  | "short_link_create"
  | "product_email_draft"
  | "product_email_send_test"
  | "product_email_send"
  | "product_email_view"
  | "activation_token_generate"
  | "activation_token_view"
  | "email_suppression_view"
  | "email_suppression_create"
  | "email_suppression_delete"
  | "announcement_list"
  | "announcement_view"
  | "announcement_create"
  | "announcement_update"
  | "announcement_delete"
  | "announcement_broadcast"
  | "learning_topics_view"
  | "learning_topics_create"
  | "learning_topics_update"
  | "learning_topics_delete"
  | "learning_topics_broadcast"
  | "release_notes_week_list"
  | "release_notes_list"
  | "release_notes_view"
  | "release_notes_item_update"
  | "release_notes_item_create"
  | "release_notes_item_delete"
  | "release_notes_items_reorder"
  | "trial_eligibility_view"
  | "trial_eligibility_override_grant"
  | "trial_eligibility_override_revoke"
  | "refund_eligibility_view"
  | "refund_eligibility_override_grant"
  | "refund_eligibility_override_revoke"
  // A7Y-1274 — Internal Support Access (grant/revoke + general org-update bucket).
  | "org_update"
  // A7Y-1495 — SaaS spend dashboard read.
  | "saas_spend_view"
  | "platform_health_view"
  // A7Y-5454 — release a held audit email from the PRISM workbench.
  | "prism_pre_send_release"
  // A7Y-1494 — Audit spend dashboard read.
  | "audit_spend_view"
  // A7Y-5556 — AI APIs admin section read.
  | "ai_apis_view"
  // A7Y-2054 — Finance P&L dashboard read.
  | "finance_view"
  // A7Y-4883 — Stats → Keyword Database read.
  | "keyword_database_view"
  | "stats_overview_view"
  // A7Y-1898 — Affiliate admin tooling (console-decorator actions; the durable
  // money/destructive trail is the separate AffiliateAuditLog collection).
  | "affiliate_view"
  | "affiliate_approve"
  | "affiliate_disable"
  | "affiliate_reenable"
  | "affiliate_gdpr_anonymize"
  | "affiliate_suspicious_resolve"
  | "affiliate_first_commission_release"
  | "affiliate_link_create"
  | "affiliate_link_archive"
  | "affiliate_discount_set"
  | "affiliate_coupon_set"
  | "affiliate_campaign_create"
  | "affiliate_campaign_update"
  // A7Y-4291 — the admin mutation routes that carried no audit decorator.
  | "messaging_thread_reply"
  | "messaging_thread_update"
  | "messaging_thread_read"
  | "messaging_stream_token"
  | "email_suppression_link_generate"
  | "audit_test_run"
  | "project_vendor_number_update"
  | "project_store_link_recheck"
  | "service_health_refresh"
  | "channel_console_refresh"
  | "autobuild_quota_refresh"
  | "autobuild_settings_update"
  | "autobuild_pause"
  | "affiliate_campaign_set_default"
  | "affiliate_commission_void"
  | "affiliate_commission_adjust"
  | "affiliate_payout_approve"
  | "affiliate_payout_mark_external"
  | "affiliate_payout_execute"
  | "affiliate_export"
  // A7Y-2066 / A7Y-2069 — admin blog CMS (posts, revisions, preview, images).
  | "blog_list"
  | "blog_view"
  | "blog_create"
  | "blog_update"
  | "blog_delete"
  | "blog_publish"
  | "blog_schedule"
  | "blog_unpublish"
  | "blog_revisions_view"
  | "blog_restore"
  | "blog_preview_token"
  | "blog_draft_view"
  | "blog_image_upload"
  | "blog_image_list"
  // A7Y-4469 — archive/duplicate, the author registry, and the generic
  // content-render/re-render/migration admin endpoints.
  | "blog_archive"
  | "blog_duplicate"
  | "blog_author_list"
  | "blog_author_create"
  | "blog_author_update"
  | "blog_author_delete"
  | "content_render"
  | "content_rerender"
  | "content_reindex"
  | "content_migrate_blog_authors"
  // A7Y-4478 — per-person and automation content keys (/admin/content-keys/*).
  | "content_key_list"
  | "content_key_mint"
  | "content_key_revoke"
  | "support_key_list"
  | "support_key_mint"
  | "support_key_revoke"
  // A7Y-4474 — the Publishing queue and "Queue in Publishing".
  | "content_publishing_view"
  | "blog_pending_status"
  // A7Y-3870 — HELM v2 admin routes (/admin/helm/*).
  | "helm_refresh"
  | "helm_action_create"
  | "helm_action_undo"
  | "helm_config_update"
  // A7Y-4974 — the internal CRM admin routes (/admin/crm/*).
  | "crm_import"
  | "crm_account_create"
  | "crm_account_stage_set"
  | "crm_account_last_touch_set"
  | "crm_account_next_action_set"
  | "crm_account_segment_set"
  | "crm_account_priority_set"
  | "crm_account_watch_set"
  | "crm_account_close"
  | "crm_account_rename"
  | "crm_account_delete"
  | "crm_contact_upsert"
  | "crm_contact_departed"
  | "crm_contact_primary_set"
  | "crm_contact_reports_to_set"
  | "crm_contact_delete"
  | "crm_event_append"
  | "crm_event_delete"
  | "crm_roster_item_add"
  | "crm_roster_item_redate"
  | "crm_roster_item_close"
  | "crm_roster_item_link"
  | "crm_roster_item_delete"
  | "crm_slots_replace"
  | "crm_parked_replace"
  | "crm_week_plan_write"
  | "crm_follow_up_update"
  | "crm_lead_update"
  // A7Y-4470 — admin docs CMS (/admin/docs/*), the nav config, redirects and
  // the content-site reader feedback list (blog/docs).
  | "docs_list"
  | "docs_view"
  | "docs_create"
  | "docs_update"
  | "docs_delete"
  | "docs_publish"
  | "docs_publish_batch"
  | "docs_schedule"
  | "docs_unpublish"
  | "docs_archive"
  | "docs_duplicate"
  | "docs_revisions_view"
  | "docs_restore"
  | "docs_preview_token"
  | "docs_image_upload"
  | "docs_image_list"
  | "docs_pending_status"
  | "docs_nav_view"
  | "docs_nav_update"
  | "docs_redirect_list"
  | "docs_redirect_create"
  | "docs_redirect_delete"
  | "docs_suggestion_list"
  | "docs_suggestion_create"
  | "docs_suggestion_apply"
  | "docs_suggestion_dismiss"
  | "docs_suggestion_tickets"
  | "content_feedback_list"
  | "content_feedback_update";

export interface AdminAuditLog {
  id: string;
  adminUserId?: string;
  adminEmail?: string;
  action: AdminAuditAction;
  resource: string;
  resourceId?: string;
  details?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  timestamp: number | string;
  success: boolean;
  errorMessage?: string;
  /**
   * A7Y-2491: 90-day retention boundary, matching the `server_log` telemetry
   * window (A7Y-2488) so the DPA documents ONE number for all logging. Written
   * as an ISO8601 string; the Firestore TTL policy on `AdminAuditLogs` reaps on
   * this field.
   */
  expiresAt?: string;
}

export interface SupportTicketReply {
  id: string;
  ticketId: string;
  authorId: string;
  authorEmail: string;
  authorType: "admin" | "user";
  message: string;
  isInternal: boolean;
  createdAt: number | string;
}

export interface SupportTicket {
  id: string;
  userId: string;
  userEmail: string;
  orgId?: string;
  subject: string;
  description: string;
  status: "open" | "in_progress" | "waiting_on_customer" | "resolved" | "closed";
  priority: "low" | "medium" | "high" | "urgent";
  category: "billing" | "technical" | "feature_request" | "bug" | "other";
  assignedToAdminId?: string;
  createdAt: number | string;
  updatedAt: number | string;
  resolvedAt?: number | string;
  closedAt?: number | string;
  replies: SupportTicketReply[];
}

export interface SystemConfig {
  id: string;
  key: string;
  value: unknown;
  description?: string;
  updatedBy: string;
  updatedAt: number | string;
  environment: "development" | "staging" | "production";
}

export interface SystemHealthMetric {
  timestamp: number | string;
  service: string;
  status: "healthy" | "degraded" | "unhealthy";
  responseTime?: number;
  errorRate?: number;
  details?: Record<string, unknown>;
}

export interface BillingCreditIssue {
  id: string;
  orgId: string;
  amount: number;
  currency: "USD";
  reason: string;
  issuedBy: string;
  issuedAt: number | string;
  expiresAt?: number | string;
}

// Stripe Subscriptions (admin view, sourced from Stripe API)
export type StripeSubscriptionStatus =
  | "active"
  | "trialing"
  | "past_due"
  | "canceled"
  | "incomplete"
  | "incomplete_expired"
  | "unpaid"
  | "paused";

export interface AdminSubscription {
  id: string;
  status: StripeSubscriptionStatus;
  stripeEnv: "sandbox" | "production";
  customerId: string;
  customerEmail?: string;
  planId?: string;
  priceId?: string;
  orgId?: string;
  orgName?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  cancelAt?: string;
  trialStart?: string;
  trialEnd?: string;
  createdAt?: string;
  canceledAt?: string;
  metadata?: Record<string, string>;
}

export interface AdminSubscriptionDetail extends AdminSubscription {
  items: {
    id: string;
    priceId: string;
    productId: string;
    quantity: number;
    unitAmount?: number;
    currency?: string;
    interval?: string;
    intervalCount?: number;
  }[];
  latestInvoiceId?: string;
  defaultPaymentMethodId?: string;
}

// Promo Codes (admin view, sourced from Stripe API)
export type PromoCouponDuration = "once" | "repeating" | "forever";
export type PromoCouponType = "percent_off" | "extended_trial";

export interface AdminPromoCoupon {
  id: string;
  name: string;
  type: PromoCouponType;
  percentOff: number | null;
  trialDays: number | null;
  duration: PromoCouponDuration;
  durationInMonths: number | null;
  maxRedemptions: number | null;
  timesRedeemed: number;
  redeemBy: string | null;
  valid: boolean;
  createdAt: string;
  metadata: Record<string, string>;
  promoCodes: AdminPromoCode[];
}

export interface AdminPromoCode {
  id: string;
  code: string;
  couponId: string;
  active: boolean;
  maxRedemptions: number | null;
  timesRedeemed: number;
  expiresAt: string | null;
  firstTimeTransaction: boolean;
  createdAt: string;
}

/** A single row from the BigQuery a7y_data.server_log table (errors & warnings view). */
export interface AdminServerLogEvent {
  message_id: string | null;
  id: string | null;
  user_id: string | null;
  publish_time: string | null;
  event_type: string;
  message: string;
  country_ip: string | null;
  origin_host: string | null;
  data: string | null;
  error_data: string | null;
  env: string | null;
  source: string | null;
  path: string | null;
  client_id: string | null;
  ip_hash: string | null;
  os: string | null;
  browser: string | null;
  browser_version: string | null;
  manufacturer: string | null;
  model: string | null;
  os_version: string | null;
  user_agent: string | null;
  country_device: string | null;
  country_monetization: string | null;
  currency: string | null;
  city: string | null;
  region: string | null;
  region_code: string | null;
  timezone: string | null;
  continent: string | null;
  is_eu_country: boolean | null;
  colo: string | null;
  context_id: string | null;
  subscription_name: string | null;
  attributes: string | null;
  lat: number | null;
  lon: number | null;
  postal_code: string | null;
  verified_bot_category: string | null;
  client_version: string | null;
}

/** Service health status entry for a single external dependency. */
export interface AdminServiceHealthStatus {
  /** Service slug (e.g. "anthropic", "decodo", "perplexity", "clerk", "resend") */
  service: string;
  /** Current status string (e.g. "investigating", "resolved", "operational") */
  status: string | null;
  /** Normalized severity level */
  severity: "none" | "minor" | "major" | "critical" | null;
  /** Event type (incident, maintenance, component) */
  eventType: string | null;
  /** Human-readable incident/event title */
  title: string | null;
  /** Optional description snippet */
  description: string | null;
  /** Link to the status page or incident */
  url: string | null;
  /** ISO timestamp of the latest event */
  timestamp: string | null;
}

// Admin KPI Dashboard
export interface AdminKpiSnapshot {
  /** Date string in YYYY-MM-DD format */
  date: string;
  /** Active external trials; null for backfilled historical days where trial count was not available */
  activeTrials: number | null;
  /** New user signups on this date, excluding internal @apptonomy.ai accounts */
  newUsers: number;
  activeUsers: number;
  totalSessions: number;
  avgSessionsPerUser: number;
  activeSubscriptions: number;
  /** Monthly recurring revenue in cents (integer) */
  mrrCents: number;
  openSupportThreads: number;
  /** ISO 8601 timestamp when this snapshot was computed */
  computedAt: string;
}

export interface AdminKpiDashboardResponse {
  current: AdminKpiSnapshot | null;
  timeSeries: AdminKpiSnapshot[];
}

/**
 * One project whose App Store Connect credential is currently rejected by
 * Apple — surfaced from `apps.asc.store_link.permission_error` (set by the
 * scheduled-ingestion `flagAscCredentialError`/`clearAscCredentialError`
 * pair). Powers the admin dashboard's "Broken ASC Credentials" widget so
 * Yevhen/Thomas can see at a glance which customers need outreach.
 */
export interface AdminAscCredentialErrorListItem {
  projectId: string;
  orgId: string;
  appName: string;
  appId: string;
  category: AscEndpointCategory;
  statusCode: 401 | 403;
  detectedAt: number;
}

export interface AdminAscCredentialErrorsResponse {
  items: AdminAscCredentialErrorListItem[];
}

// Navigation definitions for org/admin shells
export type OrgNavSection = "primary" | "secondary";

export interface OrgNavItem {
  label: string;
  href: string;
  icon?: unknown;
  section?: OrgNavSection;
}
